// Which programs are for roles only.
//
// A program built for roles in Publish Training, of any category, is for the roles chosen
// when it was built, and for no one else: it cannot be given another audience,
// and nobody is invited to it by name or in bulk. The program holds its roles
// (roleIds, on GET /v3/admin/programs and GET /group/{grpId}), and the backend
// refuses every other way in (409). Wherever a program's audience can be
// changed or people added to it, this says whether it is one, so nobody is
// offered what would be refused.

import { useCapabilityRoleNames } from '../../../../../queries/useCapability';

export interface RoleLock {
  roleIds: string[];
  /** The roles' names, as Capability Setup has them: none while they load, or for a role since deleted. */
  roleNames: string[];
}

/** The roles a program is for (its roleIds), or null when it is not a roles-only program. */
export const roleLockOf = (
  roleIds: string[] | null | undefined,
  names: Map<string, string> | undefined
): RoleLock | null => {
  const ids = (roleIds ?? []).filter(Boolean);
  if (ids.length === 0) return null;
  return { roleIds: ids, roleNames: ids.map((id) => names?.get(id)).filter((n): n is string => Boolean(n)) };
};

/** What a roles-only program's audience reads as: its roles' names, or how many roles while they are not known. */
export const roleLockLabels = (lock: RoleLock): string[] =>
  lock.roleNames.length > 0
    ? lock.roleNames
    : [`${lock.roleIds.length} ${lock.roleIds.length === 1 ? 'Role' : 'Roles'}`];

/**
 * The roles a program is for, from its own roleIds, or null when it is not a
 * roles-only program. Their names are read once (Capability Setup's roles)
 * for every program that needs them, and only when one does.
 */
export const useRoleLock = (roleIds: string[] | null | undefined): RoleLock | null => {
  const { data: names } = useCapabilityRoleNames((roleIds ?? []).length > 0);
  return roleLockOf(roleIds, names);
};
